import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, IconButton, TextField, Typography } from "@mui/material";
import { Modal } from "app/components/Modal";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  valueForOnlyNumericInputTextFields,
  valueForOnlyStringInputTextFields,
} from "utils/helpers";

import {
  selectCanSubmit,
  selectForm,
  selectIsAddModalOpen,
  selectSaving,
} from "../../redux/selector";
import { actions } from "../../redux/slice";
import { BodyTypeForm, FieldType, FieldValues } from "../../types";
import { useStyles } from "./styles";

type FieldConfig = {
  key: keyof BodyTypeForm;
  label: string;
};

const FIELDS: FieldConfig[] = [
  { key: "bodyTypeName", label: "DESCRIPTION" },
];

const MODAL_I18N_KEY = "BODY_TYPE.MODAL";

const AddBodyTypeModal = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const dispatch = useDispatch();
  const open = useSelector(selectIsAddModalOpen);
  const form = useSelector(selectForm);
  const saving = useSelector(selectSaving);
  const canSubmit = useSelector(selectCanSubmit);

  const onClose = () => {
    if (saving) return;
    dispatch(actions.closeAddModal());
  };

  const setField = (name: keyof BodyTypeForm, value: string) =>
    dispatch(
      actions.setFormField({
        name,
        value:
          FieldValues[name] === FieldType.Number
            ? valueForOnlyNumericInputTextFields(value)
            : valueForOnlyStringInputTextFields(value),
      }),
    );

  return (
    <Modal open={open} onClose={onClose}>
      <Box className={styles.modal}>
        <Box className={styles.header}>
          <Typography className={styles.title}>
            {t(`${MODAL_I18N_KEY}.ADD_TITLE`)}
          </Typography>
          <IconButton
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t("BUTTONS.CANCEL")}
            disabled={saving}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className={styles.body}>
          {FIELDS.map(({ key, label }) => (
            <Box key={key} className={styles.field}>
              <Typography className={styles.label}>
                {t(`${MODAL_I18N_KEY}.FIELDS.${label}`)}
              </Typography>
              <TextField
                className={styles.input}
                fullWidth
                variant="outlined"
                placeholder={t(`${MODAL_I18N_KEY}.PLACEHOLDERS.ENTER_${label}`)}
                value={form[key]}
                disabled={saving}
                InputProps={{ notched: false }}
                {...(FieldValues[key] === FieldType.Number
                  ? { inputMode: "numeric" as const }
                  : {})}
                onChange={(event) => setField(key, event.target.value)}
              />
            </Box>
          ))}
        </Box>

        <Box className={styles.footer}>
          <Button
            className={styles.cancelButton}
            variant="outlined"
            onClick={onClose}
            disabled={saving}
          >
            {t("BUTTONS.CANCEL")}
          </Button>
          <Button
            className={styles.addButton}
            variant="contained"
            disableElevation
            disabled={!canSubmit || saving}
            onClick={() => dispatch(actions.add())}
          >
            {t("BUTTONS.ADD")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddBodyTypeModal;
